import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Flame, Star } from 'lucide-react';
import { api } from '../api';
import { usePlatform } from '../store';
import { gameTitle } from '../game-presentation';
import { GameArt } from './GameCard';
import type { Rankings } from '../../shared/rankings';
import './leaderboard.css';

export function Leaderboard({ full = false }: { full?: boolean }) {
  const { data } = usePlatform();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'hot' | 'rated'>(params.get('tab') === 'rated' ? 'rated' : 'hot');
  const [ranking, setRanking] = useState<Rankings | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError(false);
    api<Rankings>('/rankings')
      .then((r) => {
        if (active) setRanking(r);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  const entries = (ranking?.[tab] || []).flatMap((entry) => {
    const game = data?.games.find((g) => g.id === entry.gameId);
    return game ? [{ ...entry, game }] : [];
  });
  return (
    <section
      className={`island-leaderboard${full ? ' leaderboard-full' : ''}`}
      aria-label="游戏排行榜"
    >
      {full && <h1>游戏排行榜</h1>}
      <div className="leaderboard-tabs" aria-label="榜单类型">
        {(['hot', 'rated'] as const).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            className={tab === key ? 'active' : ''}
            onClick={() => {
              setTab(key);
              if (full) setParams({ tab: key });
            }}
          >
            {key === 'hot' ? <Flame size={18} /> : <Star size={18} />}
            {key === 'hot' ? '热玩榜' : '好评榜'}
          </button>
        ))}
      </div>
      <p
        className="leaderboard-caption"
        title={
          tab === 'hot'
            ? '近 7 天开始且有游玩时长的记录，按账号或访客标识去重'
            : '综合平均评分与评价人数排序'
        }
      >
        {tab === 'hot' ? '近 7 天游玩人数' : '综合评分与评价人数'}
      </p>
      <div className="leaderboard-body" aria-live="polite">
        {error ? (
          <div className="leaderboard-empty">
            暂时无法加载<button onClick={() => setAttempt((a) => a + 1)}>重试</button>
          </div>
        ) : !ranking || !data ? (
          <div className="leaderboard-empty">正在加载…</div>
        ) : !entries.length ? (
          <div className="leaderboard-empty">{tab === 'hot' ? '暂无游玩数据' : '暂无评分'}</div>
        ) : (
          <ol className="leaderboard-list">
            {entries.slice(0, full ? entries.length : 5).map((entry, index) => (
              <li key={entry.gameId}>
                <Link to={'/game/' + entry.game.slug}>
                  <span className={`leaderboard-rank rank-${index + 1}`}>{index + 1}</span>
                  <GameArt game={entry.game} />
                  <span className="leaderboard-name">{gameTitle(entry.game)}</span>
                  <span className={`leaderboard-value ${tab}`}>
                    {tab === 'hot' ? (
                      <>
                        {entry.players.toLocaleString('zh-CN')}
                        <small>人</small>
                      </>
                    ) : (
                      <>
                        {entry.rating.toFixed(1)}
                        <small>{entry.ratingCount} 人评价</small>
                      </>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>
      {!full && (
        <Link className="leaderboard-more" to={'/rankings?tab=' + tab}>
          查看完整榜单
          <ChevronRight size={15} />
        </Link>
      )}
    </section>
  );
}
